import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Brain, Loader2, Plus, Download, FileText, FileCode } from 'lucide-react';

function AdminPrompts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [prompts, setPrompts] = useState([]);
  const [filteredType, setFilteredType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchPrompts();
  }, [user, navigate]);
  
  // Download prompt as Markdown
  const downloadAsMarkdown = (prompt) => {
    const content = `# ${prompt.name}

## Metadata
- **Type:** ${prompt.assessmentType === 'fear' ? 'EDGE Diagnostic' : 'CORE Diagnostic'}
- **Version:** ${prompt.versionNumber}
- **Status:** ${prompt.isActive ? 'Active' : 'Inactive'}
- **Created:** ${formatDate(prompt.createdAt)}
${prompt.notes ? `- **Notes:** ${prompt.notes}` : ''}

## Prompt Text

\`\`\`
${prompt.promptText}
\`\`\`
`;
    
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${prompt.assessmentType}_prompt_v${prompt.versionNumber}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded as Markdown');
  };
  
  // Download prompt as plain text
  const downloadAsText = (prompt) => {
    const content = `${prompt.name}
${'='.repeat(prompt.name.length)}

Type: ${prompt.assessmentType === 'fear' ? 'EDGE Diagnostic' : 'CORE Diagnostic'}
Version: ${prompt.versionNumber}
Status: ${prompt.isActive ? 'Active' : 'Inactive'}
Created: ${formatDate(prompt.createdAt)}
${prompt.notes ? `Notes: ${prompt.notes}` : ''}

--- PROMPT TEXT ---

${prompt.promptText}
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${prompt.assessmentType}_prompt_v${prompt.versionNumber}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded as Text');
  };
  
  // Download all prompts as JSON
  const downloadAllPrompts = () => {
    const exportData = prompts.map(p => ({
      name: p.name,
      type: p.assessmentType,
      typeName: p.assessmentType === 'fear' ? 'EDGE Diagnostic' : 'CORE Diagnostic',
      version: p.versionNumber,
      isActive: p.isActive,
      createdAt: p.createdAt,
      notes: p.notes,
      promptText: p.promptText
    }));
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_prompts_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded all prompts');
  };
  
  const fetchPrompts = async () => {
    try {
      const response = await axios.get('/api/admin/prompts');
      setPrompts(response.data.prompts);
    } catch (error) {
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };
  
  const activatePrompt = async (promptId) => {
    try {
      await axios.post(`/api/admin/prompts/${promptId}/activate`);
      toast.success('Prompt activated');
      fetchPrompts();
    } catch (error) {
      toast.error('Failed to activate prompt');
    }
  };
  
  const deletePrompt = async (promptId) => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;
    
    try {
      await axios.delete(`/api/admin/prompts/${promptId}`);
      toast.success('Prompt deleted');
      fetchPrompts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete prompt');
    }
  };
  
  const filteredPrompts = prompts.filter(p => {
    if (filteredType === 'all') return true;
    return p.assessmentType === filteredType;
  });
  
  const groupedPrompts = filteredPrompts.reduce((acc, prompt) => {
    if (!acc[prompt.assessmentType]) {
      acc[prompt.assessmentType] = [];
    }
    acc[prompt.assessmentType].push(prompt);
    return acc;
  }, {});
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="admin-prompts-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="admin-prompts-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Brain className="w-6 h-6 text-primary" />
            Prompt Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage assessment prompt versions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={downloadAllPrompts}>
                <FileCode className="w-4 h-4 mr-2" />
                Export All as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create New Prompt
          </Button>
        </div>
      </div>
      
      {/* Type Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {['all', 'fear', 'archetype'].map(type => (
          <button
            key={type}
            onClick={() => setFilteredType(type)}
            className={`px-4 py-2 text-sm font-medium transition-smooth border-b-2 ${
              filteredType === type
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {type === 'all' ? 'All Prompts' : type === 'fear' ? 'EDGE Diagnostic' : 'CORE Diagnostic'}
          </button>
        ))}
      </div>
      
      {/* Prompts List */}
      {Object.entries(groupedPrompts).map(([type, typePrompts]) => (
        <div key={type} className="mb-8">
          <h2 className="h3 text-xl mb-4 capitalize">
            {type === 'fear' ? 'EDGE Diagnostic' : type === 'archetype' ? 'CORE Diagnostic' : type} Prompts
          </h2>
          
          <div className="space-y-4">
            {typePrompts.map(prompt => (
              <motion.div
                key={prompt.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card border border-border rounded-[var(--radius)] p-6 elev-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{prompt.name}</h3>
                      <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                        v{prompt.versionNumber}
                      </span>
                      {prompt.isActive && (
                        <span className="text-xs px-2 py-1 rounded bg-success/10 text-success border border-success/30">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Created {formatDate(prompt.createdAt)}
                    </p>
                    {prompt.notes && (
                      <p className="text-sm mt-2 text-muted-foreground italic">{prompt.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => downloadAsMarkdown(prompt)}>
                          <FileText className="w-4 h-4 mr-2" />
                          Download as Markdown
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => downloadAsText(prompt)}>
                          <FileCode className="w-4 h-4 mr-2" />
                          Download as Text
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPrompt(prompt)}
                    >
                      View
                    </Button>
                    {!prompt.isActive && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => activatePrompt(prompt.id)}
                        >
                          Activate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive border-destructive/30 hover:bg-destructive/10"
                          onClick={() => deletePrompt(prompt.id)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="p-4 bg-muted/30 rounded-[var(--radius)] text-sm font-mono overflow-x-auto max-h-40 overflow-y-auto">
                  {prompt.promptText.substring(0, 300)}...
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      ))}
      
      {filteredPrompts.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          No prompts found. Create one to get started.
        </div>
      )}
      
      {editingPrompt && (
        <PromptModal
          prompt={editingPrompt}
          onClose={() => setEditingPrompt(null)}
          onSave={() => {
            setEditingPrompt(null);
            fetchPrompts();
          }}
        />
      )}
      
      {showCreateModal && (
        <CreatePromptModal
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            fetchPrompts();
          }}
        />
      )}
    </div>
  );
}

function PromptModal({ prompt, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-[var(--radius)] p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto elev-2"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="h2 text-2xl mb-2">{prompt.name}</h2>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span>Version {prompt.versionNumber}</span>
              <span>•</span>
              <span className="capitalize">{prompt.assessmentType === 'fear' ? 'EDGE' : 'CORE'} Diagnostic</span>
              <span>•</span>
              <span>Created {formatDate(prompt.createdAt)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            ✕
          </button>
        </div>
        
        {prompt.notes && (
          <div className="mb-4 p-3 bg-muted/30 rounded-[var(--radius)] text-sm">
            <strong>Notes:</strong> {prompt.notes}
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Prompt Text:</label>
          <div className="p-4 bg-muted/30 rounded-[var(--radius)] text-sm font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
            {prompt.promptText}
          </div>
        </div>
        
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function CreatePromptModal({ onClose, onCreate }) {
  const [assessmentType, setAssessmentType] = useState('fear');
  const [name, setName] = useState('');
  const [promptText, setPromptText] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleCreate = async () => {
    if (!name || !promptText) {
      toast.error('Name and prompt text are required');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post('/api/admin/prompts', {
        assessmentType,
        name,
        promptText,
        notes
      });
      toast.success('Prompt created successfully');
      onCreate();
    } catch (error) {
      toast.error('Failed to create prompt');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-[var(--radius)] p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto elev-2"
      >
        <h2 className="h2 text-2xl mb-6">Create New Prompt Version</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Assessment Type</label>
            <select
              value={assessmentType}
              onChange={(e) => setAssessmentType(e.target.value)}
              className="w-full h-10 px-3 rounded-[var(--radius)] border border-input bg-background focus-ring"
            >
              <option value="fear">EDGE Diagnostic</option>
              <option value="archetype">CORE Diagnostic</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., EDGE Diagnostic v2 - Updated Questions"
              className="w-full h-10 px-3 rounded-[var(--radius)] border border-input bg-background focus-ring"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Prompt Text</label>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="Enter the full system prompt for the LLM..."
              rows={15}
              className="w-full px-3 py-2 rounded-[var(--radius)] border border-input bg-background focus-ring font-mono text-sm resize-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What changed in this version?"
              rows={3}
              className="w-full px-3 py-2 rounded-[var(--radius)] border border-input bg-background focus-ring resize-none"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Prompt'
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

export default AdminPrompts;
